import React, { useImperativeHandle, useState, useRef } from "react";
import clsx from "clsx";
import { Grid } from "@mui/material";
import { MASK_TYPE, AutocompleteField, Button, Card, MenuItem, SelectField, TextField, Typography } from "@curves/ui-base";
import usePeopleApi from "@apis/usePeopleApi";
import { isEqual, refIsRequiredError } from "@util/utils";
import { PEOPLE_TYPE, RECEIVER_TYPE } from "@icoach/constants/dataEnums";

// 會員
// 增加是否訂閱智能月費API
export const FieldByMember = React.forwardRef((props, ref) => {
	const { defaultValue, readOnly } = props;
	const { people: memberOptions = [] } = usePeopleApi(PEOPLE_TYPE.ALLMEMBER);
	const memberRef = useRef();

	useImperativeHandle(
		ref,
		() => ({
			getResult: () => {
				return {
					receiverID: memberRef.current.getResult().id,
				};
			},
			isError: () => refIsRequiredError(memberRef),
		}),
		// eslint-disable-next-line
		[]
	);

	return (
		<React.Fragment>
			<Grid xs={5} item>
				<AutocompleteField
					key={memberOptions}
					className="flex-auto"
					defaultValue={defaultValue}
					label="姓名/手機號碼/會員編號(擇一即可)"
					ref={memberRef}
					options={memberOptions}
					optionKeys={["name", "nickName", "mobile", "no"]}
					optionMaskKeys={["maskName", "nickName", "maskMobile", "no"]}
					primaryKey="id"
					InputProps={{
						disabled: readOnly,
					}}
					fullWidth
					required
				/>
			</Grid>
		</React.Fragment>
	);
});

// 非會員
const FieldByNonMember = React.forwardRef((props, ref) => {
	const { readOnly } = props;
	const nameRef = useRef(null);
	const telRef = useRef(null);

	useImperativeHandle(
		ref,
		() => ({
			getResult: () => {
				return {
					receiverName: nameRef.current.getResult(),
					receiverPhone: telRef.current.getResult(),
				};
			},
			isError: () => refIsRequiredError(nameRef, telRef),
		}),
		// eslint-disable-next-line
		[]
	);

	return (
		<React.Fragment>
			<Grid xs={3} item>
				<TextField label="姓名" ref={nameRef} disabled={readOnly} fullWidth required />
			</Grid>
			<Grid xs={3} item>
				<TextField label="手機號碼" ref={telRef} maskType={MASK_TYPE.MOB} disabled={readOnly} fullWidth required />
			</Grid>
		</React.Fragment>
	);
});

// 跨店會員
const FieldByOtherClubMember = React.forwardRef((props, ref) => {
	const { clubOptions, readOnly } = props;
	const clubRef = useRef(null);
	const nameRef = useRef(null);
	const telRef = useRef(null);

	useImperativeHandle(
		ref,
		() => ({
			getResult: () => {
				let result = {};
				if (clubRef.current && clubRef.current.getResult) result.clubID = clubRef.current.getResult().value;
				if (nameRef.current && nameRef.current.getResult) result.receiverName = nameRef.current.getResult();
				if (telRef.current && telRef.current.getResult) result.receiverPhone = telRef.current.getResult();
				return result;
			},
			isError: () => refIsRequiredError(clubRef, nameRef, telRef),
		}),
		// eslint-disable-next-line
		[]
	);

	return (
		<React.Fragment>
			<Grid xs={3} item>
				<AutocompleteField
					label="分店"
					ref={clubRef}
					options={clubOptions}
					optionKeys={["text"]}
					primaryKey="id"
					InputProps={{
						disabled: readOnly,
					}}
					inputProps={{
						placeholder: "請輸入分店名稱",
					}}
					fullWidth
					required
				/>
			</Grid>
			<Grid xs={3} item>
				<TextField label="姓名" ref={nameRef} disabled={readOnly} fullWidth required />
			</Grid>
			<Grid xs={3} item>
				<TextField label="手機號碼" ref={telRef} maskType={MASK_TYPE.MOB} disabled={readOnly} fullWidth required />
			</Grid>
		</React.Fragment>
	);
});

// 教練
const FieldByEmployee = React.forwardRef((props, ref) => {
	const { employeeOptions, readOnly } = props;
	const employeeRef = useRef();

	useImperativeHandle(
		ref,
		() => ({
			getResult: () => {
				return {
					receiverID: employeeRef.current.getResult(),
				};
			},
			isError: () => refIsRequiredError(employeeRef),
		}),
		// eslint-disable-next-line
		[]
	);

	return (
		<React.Fragment>
			<Grid xs={5} item>
				<SelectField label="姓名" ref={employeeRef} disabled={readOnly} fullWidth displayEmpty required>
					<MenuItem value={""} disabled>
						<em>請選擇</em>
					</MenuItem>
					{Array.isArray(employeeOptions) &&
						employeeOptions
							.filter(item => item || !item.disabled)
							.map(item => (
								<MenuItem key={item.value} value={item.value}>
									{item.text}
								</MenuItem>
							))}
				</SelectField>
			</Grid>
		</React.Fragment>
	);
});

// 店舖
const FieldByClub = React.forwardRef((props, ref) => {
	const { clubOptions, readOnly } = props;
	const clubRef = useRef();

	useImperativeHandle(
		ref,
		() => ({
			getResult: () => {
				let result = {};
				if (clubRef.current && clubRef.current.getResult) result.targetClubID = clubRef.current.getResult().value;
				return result;
			},
			isError: () => refIsRequiredError(clubRef),
		}),
		// eslint-disable-next-line
		[]
	);

	return (
		<React.Fragment>
			<Grid xs={3} item>
				<AutocompleteField
					label="店舖名稱"
					ref={clubRef}
					options={clubOptions}
					optionKeys={["text"]}
					primaryKey="id"
					InputProps={{
						disabled: readOnly,
					}}
					fullWidth
					required
				/>
			</Grid>
		</React.Fragment>
	);
});

const SetTargetSearchBar = React.forwardRef((props, ref) => {
	const { className, title, resource = {}, isShowContent, setShowContent, doConfirmRemitApi, doConfirmMemberOrderApi } = props;
	const [receiver, setReceiver] = useState(0);
	const { receiverOptions = [], clubOptions = [], coachOptions: employeeOptions = [] } = resource;
	const receiverRef = useRef();
	const fieldRef = useRef();
	let isOtherClubMember = isEqual(receiver, RECEIVER_TYPE.CROSSMEMBER);

	const isError = (fieldRef, receiverRef) => refIsRequiredError(fieldRef, receiverRef);

	const handleReceiverChange = (e, n, v) => {
		setReceiver(Number(v));
	};

	const handleClickConfirm = () => {
		if (!isShowContent) {
			if (!isError(fieldRef, receiverRef)) {
				if (isEqual(receiver, RECEIVER_TYPE.MEMBER)) {
					let memberID = fieldRef.current.getResult()?.receiverID;
					doConfirmMemberOrderApi(memberID);
				} else if (isEqual(receiver, RECEIVER_TYPE.CLUB)) {
					let clubID = fieldRef.current.getResult().targetClubID;
					doConfirmRemitApi(clubID);
				} else if (isEqual(receiver, RECEIVER_TYPE.HEADQUARTER)) {
					doConfirmRemitApi(0);
				}
				setShowContent(true);
			}
		} else {
			setShowContent(false);
		}
	};

	useImperativeHandle(
		ref,
		() => ({
			getResult: () => {
				let result = {};
				if (receiverRef.current) result.receiverType = receiverRef.current.getResult();
				if (fieldRef.current) Object.assign(result, fieldRef.current.getResult());
				return result;
			},
			isError: () => isError(fieldRef, receiverRef),
			getReceiver: () => receiver,
		}),
		// eslint-disable-next-line
		[receiver]
	);

	return (
		<Card className={clsx(className)}>
			<Card.CardContent className="px-4 pt-2 pb-3">
				<Typography className="mb-1" variant="h6" as="h3">
					{title}
				</Typography>
				<Grid spacing={3} container>
					<Grid xs={3} item>
						<SelectField label="對象" ref={receiverRef} onChange={handleReceiverChange} fullWidth disabled={isShowContent} displayEmpty required>
							<MenuItem value={""} disabled>
								<em>請選擇</em>
							</MenuItem>
							{Array.isArray(receiverOptions) &&
								receiverOptions
									.filter(item => item || !item.disabled)
									.map(item => (
										<MenuItem key={item.value} value={item.value}>
											{item.text}
										</MenuItem>
									))}
						</SelectField>
					</Grid>
					{isEqual(receiver, RECEIVER_TYPE.MEMBER) && <FieldByMember ref={fieldRef} readOnly={isShowContent} />}
					{isEqual(receiver, RECEIVER_TYPE.NONMEMBER) && <FieldByNonMember ref={fieldRef} readOnly={isShowContent} />}
					{isEqual(receiver, RECEIVER_TYPE.CROSSMEMBER) && <FieldByOtherClubMember ref={fieldRef} clubOptions={clubOptions} readOnly={isShowContent} />}
					{isEqual(receiver, RECEIVER_TYPE.COACH) && <FieldByEmployee ref={fieldRef} employeeOptions={employeeOptions} readOnly={isShowContent} />}
					{isEqual(receiver, RECEIVER_TYPE.CLUB) && <FieldByClub ref={fieldRef} clubOptions={clubOptions} readOnly={isShowContent} />}
					<Grid
						className={clsx({
							"ml-auto": isOtherClubMember,
							"mt-4": !isOtherClubMember,
						})}
						xs={1}
						item
					>
						<Button className="white-space-nowrap mb-1" variant="contained" onClick={handleClickConfirm}>
							{isShowContent ? "重設" : "設定"}
						</Button>
					</Grid>
				</Grid>
			</Card.CardContent>
		</Card>
	);
});

export default SetTargetSearchBar;
